// third-party
import { merge } from 'lodash-es';

// project imports
import Badge from './Badge';
import Button from './Button';
import ButtonBase from './ButtonBase';
import CardContent from './CardContent';
import Checkbox from './Checkbox';
import Chip from './Chip';
import Drawer from './Drawer';
import FormHelperText from './FormHelperText';
import IconButton from './IconButton';
import InputLabel from './InputLabel';
import LinearProgress from './LinearProgress';
import Link from './Link';
import ListItemButton from './ListItemButton';
import ListItemIcon from './ListItemIcon';
import OutlinedInput from './OutlinedInput';
import Tab from './Tab';
import TableBody from './TableBody';
import TableCell from './TableCell';
import TableHead from './TableHead';
import TableRow from './TableRow';
import Tabs from './Tabs';
import Tooltip from './Tooltip';
import Typography from './Typography';
import XDatePickers from './XDatePickers';

// ==============================|| OVERRIDES - MAIN ||============================== //

export default function ComponentsOverrides(theme) {
  return merge(
    Badge(theme),
    Button(theme),
    ButtonBase(),
    CardContent(),
    Checkbox(theme),
    Chip(theme),
    Drawer(),
    FormHelperText(),
    IconButton(theme),
    InputLabel(theme),
    LinearProgress(),
    Link(),
    ListItemButton(theme),
    ListItemIcon(theme),
    OutlinedInput(theme),
    Tab(theme),
    TableBody(theme),
    TableCell(theme),
    TableHead(theme),
    TableRow(),
    Tabs(),
    Tooltip(theme),
    Typography(),
    XDatePickers(theme),
    // ─── Premium global overrides ───
    {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: '0 1px 2px rgba(15, 45, 32, 0.04), 0 4px 20px rgba(15, 45, 32, 0.05)',
            border: '1px solid rgba(31, 91, 64, 0.07)',
            backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #fcfefd 100%)',
            transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
            animation: 'fadeInUp 0.35s ease-out both',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(15, 45, 32, 0.07), 0 12px 32px rgba(15, 45, 32, 0.06)',
              borderColor: 'rgba(31, 91, 64, 0.12)'
            }
          }
        }
      },
      MuiTextField: {
        defaultProps: {
          size: 'small'
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          rounded: {
            borderRadius: 16,
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: '20px 24px 12px',
          },
          title: {
            fontWeight: 600,
            fontSize: '0.9375rem',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
            animation: 'scaleIn 0.2s ease-out both',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            fontSize: '1.125rem',
            letterSpacing: '-0.01em',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(16px) saturate(180%)',
            backgroundColor: 'rgba(255, 255, 255, 0.88)',
            borderBottom: '1px solid rgba(31, 91, 64, 0.08)',
            boxShadow: '0 1px 0 rgba(31, 91, 64, 0.04)'
          }
        }
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '64px !important'
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: 'none',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
            border: '1px solid rgba(31, 91, 64, 0.06)',
            animation: 'scaleIn 0.15s ease-out both',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 6px',
            padding: '8px 12px',
            fontSize: '0.8125rem',
            fontWeight: 500,
            transition: 'background-color 0.15s ease',
            '&:hover': {
              backgroundColor: 'rgba(31, 91, 64, 0.06)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(31, 91, 64, 0.1)',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(31, 91, 64, 0.14)',
              },
            },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            fontSize: '0.8125rem',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            fontWeight: 500,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: 'rgba(31, 91, 64, 0.06)',
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            padding: 8,
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            '& .MuiBreadcrumbs-ol': {
              margin: 0,
              padding: 0,
              flexWrap: 'wrap'
            },
            '& .MuiBreadcrumbs-li': {
              display: 'inline-flex',
              alignItems: 'center'
            },
            '& .MuiBreadcrumbs-separator': {
              mx: 0.5,
              color: 'rgba(31, 91, 64, 0.35)'
            }
          }
        }
      },
    }
  );
}
